import { useState, useEffect } from "react"
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Filter,
  Plus,
  Eye,
  X,
  ChevronDown,
  Briefcase,
  RotateCcw,
} from 'lucide-react'
import { toast } from 'sonner'
import { onboardingService } from '../services/onboarding'
import { useCurrentOrgId } from '../contexts/AuthContext'
import { apiErrorMessage } from '../utils/apiError'
import { projectsService, Project, ProjectSummary } from '../services/projects'
import { useLabels } from '../contexts/LabelsContext'
import { StatusBadge } from '../components/StatusBadge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import type { ApiErrorLike } from '../utils/apiError'

export default function ProjectList() {
  const navigate = useNavigate()
  const { label } = useLabels()
  const [projects, setProjects] = useState<Project[]>([])
  const orgId = useCurrentOrgId()
  const [loadingSample, setLoadingSample] = useState(false)
  const hasSample = projects.some(p => p.name.startsWith('Sample: '))

  // Loads the sample portfolio when none is present, removes it otherwise
  // (backend: POST /onboarding/progress/{org}/load-demo toggles).
  const toggleSample = async () => {
    if (orgId == null) return
    setLoadingSample(true)
    try {
      const progress = await onboardingService.toggleDemo(orgId)
      toast.success(progress.demo_data_loaded
        ? 'Sample portfolio loaded. Remove it any time with "Remove sample data".'
        : 'Sample data removed. Your own projects are untouched.')
      await fetchProjects()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not update the sample portfolio.'))
    } finally {
      setLoadingSample(false)
    }
  }
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [showDeleted, setShowDeleted] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [summary, setSummary] = useState<ProjectSummary>({ total: 0, by_status: {} })
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchProjects()
  }, [filterStatus, showDeleted])

  // Counts come from the server-side summary (unfiltered tenant set), not the
  // status-filtered display list — so a status filter never zeroes the cards.
  useEffect(() => {
    fetchSummary()
  }, [])

  const fetchSummary = async () => {
    try {
      const data = await projectsService.summary()
      setSummary(data)
    } catch (error) {
      console.error('Error fetching project summary:', error)
    }
  }

  const fetchProjects = async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number | boolean> = {}
      if (filterStatus !== 'all') {
        params.status = filterStatus
      }
      if (showDeleted) {
        params.include_deleted = true
      }
      const data = await projectsService.list(params)
      setProjects(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching projects:', error)
      toast.error('Failed to load projects. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await projectsService.delete(deleteTarget.id)
      toast.success('Project moved to Recycle Bin')
      setDeleteTarget(null)
      fetchProjects()
      fetchSummary()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(error.response?.data?.detail || 'Failed to delete project')
    } finally {
      setDeleting(false)
    }
  }

  const handleRestore = async (id: number) => {
    try {
      await projectsService.restore(id)
      toast.success('Project restored successfully')
      fetchProjects()
      fetchSummary()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(error.response?.data?.detail || 'Failed to restore project')
    }
  }

  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesSearch
  })

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-700 border-red-300'
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-300'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString()
  }

  const formatCurrency = (amount: string | null) => {
    if (!amount) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(amount))
  }

  const stats = {
    total: summary.total,
    active: summary.by_status['active'] ?? 0,
    planning: summary.by_status['planning'] ?? 0,
    onHold: summary.by_status['on_hold'] ?? 0,
    completed: summary.by_status['completed'] ?? 0,
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-[#f0f4f8] min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#102a43] via-[#486581] to-[#102a43] rounded-xl p-4 sm:p-6 text-white shadow-lg border border-[#829ab1]/20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl mb-1 sm:mb-2">{label('project')} Portfolio</h1>
            <p className="text-sm sm:text-base text-[#bcccdc]">Manage and track all your {label('project', 'plural').toLowerCase()} in one place</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasSample && (
              <button
                onClick={toggleSample}
                disabled={loadingSample}
                className="min-h-[44px] px-4 py-2 border border-[#bcccdc] text-white hover:bg-white/10 rounded-lg transition-colors text-sm disabled:opacity-50"
              >
                {loadingSample ? 'Removing…' : 'Remove sample data'}
              </button>
            )}
            <button
              onClick={() => navigate('/project/create')}
              className="flex items-center gap-2 min-h-[44px] px-4 sm:px-6 py-2 sm:py-3 bg-[#DAA520] hover:bg-[#e58900] text-[#102a43] rounded-lg transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Create New {label('project')}
            </button>
          </div>
        </div>
        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: `Total ${label('project', 'plural')}`, value: stats.total.toString() },
            { label: 'Active', value: stats.active.toString() },
            { label: 'Planning', value: stats.planning.toString() },
            { label: 'On Hold', value: stats.onHold.toString() },
            { label: 'Completed', value: stats.completed.toString() },
          ].map((stat, index) => (
            <div key={index} className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
              <div className="text-sm text-[#bcccdc] mb-2">{stat.label}</div>
              <div className="text-2xl font-semibold text-white">{stat.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-xl border-2 border-[#bcccdc] shadow-md">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#829ab1] w-5 h-5" />
            <input
              type="text"
              placeholder="Search projects by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
            />
          </div>

          {/* Filter Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 border-2 border-[#bcccdc] rounded-lg hover:bg-[#f0f4f8] transition-colors flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
              <ChevronDown
                className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
              />
            </button>
            <button
              onClick={() => setShowDeleted(!showDeleted)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showDeleted
                  ? 'bg-[#486581] text-white'
                  : 'border-2 border-[#bcccdc] hover:bg-[#f0f4f8]'
              }`}
            >
              {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
            </button>
          </div>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-[#f0f4f8]">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                  filterStatus === 'all'
                    ? 'bg-[#486581] text-white'
                    : 'bg-[#f0f4f8] text-[#486581] hover:bg-[#e1e8ed]'
                }`}
              >
                All Statuses
              </button>
              {['planning', 'active', 'on_hold', 'completed', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors capitalize ${
                    filterStatus === status
                      ? 'bg-[#486581] text-white'
                      : 'bg-[#f0f4f8] text-[#486581] hover:bg-[#e1e8ed]'
                  }`}
                >
                  {status.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#486581] mx-auto"></div>
            <p className="mt-4 text-[#486581]">Loading projects...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="p-12 text-center">
            <Briefcase className="w-16 h-16 text-[#829ab1] mx-auto mb-4" />
            <p className="text-[#486581] text-lg mb-2">No projects found</p>
            <p className="text-[#829ab1] text-sm mb-4">
              {searchQuery
                ? 'Try adjusting your search criteria'
                : 'Get started by creating your first project'}
            </p>
            {!searchQuery && (
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={() => navigate('/project/create')}
                  className="px-4 py-2 bg-[#DAA520] hover:bg-[#FFD700] text-[#102a43] rounded-lg transition-colors font-medium"
                >
                  Create Project
                </button>
                <button
                  onClick={() => navigate('/import')}
                  className="px-4 py-2 border-2 border-[#bcccdc] text-[#486581] hover:bg-[#f0f4f8] rounded-lg transition-colors font-medium"
                >
                  Import from a spreadsheet
                </button>
                {summary.total === 0 && filterStatus === 'all' && orgId != null && (
                  <button
                    onClick={toggleSample}
                    disabled={loadingSample}
                    className="px-4 py-2 border-2 border-[#bcccdc] text-[#486581] hover:bg-[#f0f4f8] rounded-lg transition-colors font-medium disabled:opacity-50"
                  >
                    {loadingSample ? 'Loading sample…' : 'Explore with a sample portfolio'}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <caption className="sr-only">List of projects</caption>
              <thead className="bg-[#f0f4f8] border-b-2 border-[#bcccdc]">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#486581] uppercase tracking-wider">
                    Project
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#486581] uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#486581] uppercase tracking-wider">
                    Priority
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#486581] uppercase tracking-wider">
                    Progress
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#486581] uppercase tracking-wider">
                    Budget
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#486581] uppercase tracking-wider">
                    Dates
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-[#486581] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f0f4f8]">
                {filteredProjects.map((project) => (
                  <tr
                    key={project.id}
                    className="hover:bg-[#f0f4f8] transition-colors cursor-pointer"
                    onClick={() => navigate(`/project/${project.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#486581] rounded-lg flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="font-medium text-[#102a43]">{project.name}</div>
                          {project.description && (
                            <div className="text-sm text-[#829ab1] line-clamp-1">
                              {project.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge entityType="project" status={project.status} />
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium border capitalize ${getPriorityColor(
                          project.priority
                        )}`}
                      >
                        {project.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-[#e1e8ed] rounded-full h-2">
                          <div
                            className="bg-[#486581] h-2 rounded-full"
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-[#486581] font-medium">{project.progress}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-[#486581]">{formatCurrency(project.budget)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-[#486581]">
                        <div>{formatDate(project.start_date)}</div>
                        <div className="text-[#829ab1] text-xs">
                          {project.end_date ? `→ ${formatDate(project.end_date)}` : ''}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/project/${project.id}`)}
                          className="p-2 hover:bg-[#e1e8ed] rounded-lg transition-colors"
                          title="View project"
                        >
                          <Eye className="w-4 h-4 text-[#486581]" />
                        </button>
                        {showDeleted ? (
                          <button
                            onClick={() => handleRestore(project.id)}
                            className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                            title="Restore project"
                          >
                            <RotateCcw className="w-4 h-4 text-green-600" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setDeleteTarget(project)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete project"
                            aria-label={`Delete ${project.name}`}
                          >
                            <X className="w-4 h-4 text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Styled in-app delete confirmation (replaces native confirm). */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {label('project').toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `"${deleteTarget.name}" will be moved to the Recycle Bin. You can restore it from there until the retention window expires.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete() }}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

